import SirenParse from 'siren-parser';

import { entityStore } from '~/utility/BinSentry-ui-utility';
import { LitEntity, html, css } from '../../utility/lit-entity';
import { SirenActionMixin } from '../../utility/siren-action-mixin';
import { sharedStyles } from '../../styles/shared-styles';
import '../../common/bn-flash';
import '../../common/bn-loading';

class WorkOrderBinPhoto extends SirenActionMixin(LitEntity) {
  constructor() {
    super();
    this.loading = false;
    this.imageData = undefined;
  }

  static get properties() {
    return {
      loading: { type: Boolean },
      scanning: { type: Boolean },
      imageData: { type: String, attribute: false },
      binEntity: { type: Object, attribute: false },
      isSaving: { type: Boolean, attribute: false },
    };
  }

  static get styles() {
    return [sharedStyles, css`
      :host {
        display: block;
        box-sizing: border-box;
      }
      .actions {
        text-align: center;
        margin-bottom: 1em;
        padding: 1em;
      }
      .actions .button {
        margin: 8px 0;
      }
      .preview {
        text-align: center;
        margin: 0 auto 0;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
      }
      .preview.video {
        box-shadow: 0 0 8px var(--red-20);
      }
      .preview.image {
        box-shadow: 0 0 8px var(--green-20);
      }
      bn-loading {
        margin: 1em;
        padding: 0 2em;
        box-shadow: 0 0 3px var(--gray-50);
        background-color: var(--surface);
        text-align: center;
      }
      img,
      canvas {
        max-width: 100%;
        max-height: 400px;
      }
      .photos {
        border: 1px solid var(--blue-50);
        max-height: 650px;
        overflow-y: auto;
        padding: 8px;
        text-align: center;
      }
      .photos a {
        margin: 8px;
      }
    `];
  }

  render() {
    return html`
      <div class="subdued">
        <p>Take a picture of the entire bin. Try to ensure the BinSentry QR code sticker is visible.</p>
      </div>
      <div class="actions well">
        <button class="button button--contained" @click="${this._start}" ?disabled="${this.scanning}">Start</button>
        <button class="button button--outlined" @click="${this._stop}" ?disabled="${!this.scanning}">Stop</button>
        <button class="button button--contained" @click="${this._save}" ?disabled="${!this.scanning || this.isSaving}">Save</button>
        <div class="preview ${this.imageData ? 'image' : 'video'}">
          ${this._renderPreview()}
          <canvas height="0" width="0"></canvas>
        </div>
      </div>
      <bn-flash></bn-flash>
      <div class="photos">
        ${this._renderPhotos()}
      </div>
      <lit-entity .href="${this.binHref}" @update="${this._onUpdateBin}"></lit-entity>
    `;
  }

  _renderPreview() {
    if (this.loading) {
      return html`<bn-loading><p>Attempting to Access Camera</p></bn-loading>`;
    }
    if (this.imageData) {
      return html`
        <img src="${this.imageData}" alt="captured image" />
      `;
    }
    return html``;
  }

  _renderPhotos() {
    const photos = this.binEntity && this.binEntity.getSubEntitiesByClass('photo').reverse();
    return photos ? photos.map((photo) => html`<img src="${photo.href}"/>`) : html``;
  }

  _entityChanged(entity) {
    super._entityChanged(entity);
    this.binHref = this.getHref('https://api.binsentry.com/rel/bin');
  }

  async _onUpdateBin(e) {
    this.binEntity = e.detail.entity;
  }

  async _start() {
    const flash = this.shadowRoot.querySelector('bn-flash');
    flash.clear();
    this.scanning = true;
    this.imageData = undefined;
    if (this.video) {
      this.video.play();
      return;
    }
    try {
      this.loading = true;
      this.video = document.createElement('video');
      this.canvasElement = this.shadowRoot.querySelector('canvas');
      this.canvasContext = this.canvasElement.getContext('2d');
      this.video.srcObject = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      this.video.setAttribute('playsinline', true); // required to tell iOS safari we don't want fullscreen
      this.video.play();
      window.requestAnimationFrame(this._tick.bind(this));
    } catch (ex) {
      this.loading = false;
      this.scanning = false;
      this.video = null;
      flash.critical(ex.message);
    }
  }

  async _tick() {
    const { canvasElement, canvasContext, video } = this;
    if (!video) {
      return;
    }
    if (video.readyState === video.HAVE_ENOUGH_DATA) {
      this.loading = false;
      canvasElement.height = video.videoHeight;
      canvasElement.width = video.videoWidth;
      canvasContext.drawImage(video, 0, 0, canvasElement.width, canvasElement.height);
    }
    window.requestAnimationFrame(this._tick.bind(this));
  }

  _stop() {
    this.scanning = false;
    if (!this.video) {
      return;
    }
    this.canvasContext.clearRect(0, 0, this.canvasElement.width, this.canvasElement.height);
    this.canvasElement.height = 0;
    this.video.srcObject.getTracks().forEach((track) => track.stop());
    this.video = null;
  }

  _capture() {
    const { canvasElement } = this;
    this.imageData = canvasElement.toDataURL('image/png');
    this._stop();
  }

  async _save() {
    const flash = this.shadowRoot.querySelector('bn-flash');
    flash.clear();
    try {
      this.isSaving = true;
      this._capture();
      // get upload key
      const getImageUploadAction = this.binEntity.getActionByName('get-image-upload');
      const { entity } = await this.submitAction(getImageUploadAction);
      const uploadAction = entity.getActionByName('upload-image');
      // keep track of the key being uploaded
      this.key = uploadAction.getFieldByName('key').value;
      // upload to s3
      const imageField = uploadAction.getFieldByName('file');
      imageField.value = this._dataToBlob(this.imageData);
      await this.submitAction(uploadAction);
      flash.success('Photo saved.');
      setTimeout(this._checkImage.bind(this), 1000);
    } catch (err) {
      flash.critical(html`Failed to save photo. <pre>${err.message}</pre>`);
    } finally {
      this.isSaving = false;
    }
  }

  async _checkImage() {
    if (!this.key || !this.binHref) {
      return;
    }
    const { entity } = await entityStore.fetchWait(this.binHref);
    if (entity) {
      const siren = SirenParse(entity);
      const photos = siren.getSubEntitiesByClass('photo');
      const photo = photos.find((link) => link.href.endsWith(this.key));
      if (photo) {
        entityStore.update(this.binHref, entity);
        entityStore.invalidateHref(this.href);
        this.imageData = undefined;
        const flash = this.shadowRoot.querySelector('bn-flash');
        flash.clear();
        return;
      }
    }
    setTimeout(this._checkImage.bind(this), 1000);
  }

  _dataToBlob(dataUri) {
    const [, type, base64, data] = dataUri.match(/data:(image\/\w+)(;base64)?,(.*)/);
    const bytes = base64 ? atob(data) : unescape(data);
    const array = [];
    for (let i = 0; i < bytes.length; ++i) {
      array.push(bytes.charCodeAt(i));
    }
    return new Blob([new Uint8Array(array)], { type });
  }
}

window.customElements.define('work-order-bin-photo', WorkOrderBinPhoto);
